import { useEffect, useRef, useState } from "react";
import { Plus, Search, UserRound, X } from "lucide-react";
import { QuerySignee } from "@/api/queries/types";
import { useGetSavedQuery2 } from "@/api/queries/useGetSavedQuery";
import { cn } from "@/lib/utils";

export const SIGNEE_SEARCH_MIN_LENGTH = 4;
export const SIGNEE_SEARCH_MAX_RESULTS = 15;

export interface Signee {
  id: number;
  contactId: string;
  name: string;
}

export const SigneeSelector = ({
  signees,
  selectedSignees,
  onChange,
}: {
  signees: Signee[];
  selectedSignees: Signee[];
  onChange: (signees: Signee[]) => void;
}) => {
  const [search, setSearch] = useState("");
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [addedSignees, setAddedSignees] = useState<Signee[]>([]);
  const searchAreaRef = useRef<HTMLDivElement>(null);
  const tabNavigationRef = useRef(false);
  useEffect(() => {
    const handleOutsidePointerDown = (event: PointerEvent) => {
      if (
        event.target instanceof Node &&
        !searchAreaRef.current?.contains(event.target)
      ) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("pointerdown", handleOutsidePointerDown);
    return () =>
      document.removeEventListener("pointerdown", handleOutsidePointerDown);
  }, []);
  const { data, loading, error } = useGetSavedQuery2<QuerySignee>(
    "advPortalSigneeList",
    {}
  );
  const availableSignees = [
    ...signees,
    ...addedSignees.filter((added) => !signees.some((s) => s.id === added.id)),
  ];
  const query = search.trim().toLocaleLowerCase();
  const canSearch = query.length >= SIGNEE_SEARCH_MIN_LENGTH;
  const results = canSearch
    ? data.filter(
        (s, index) =>
          s.showData.toLocaleLowerCase().includes(query) &&
          !availableSignees.some((available) => available.id === s.id) &&
          data.findIndex((candidate) => candidate.id === s.id) === index
      )
    : [];
  const visibleResults = results.slice(0, SIGNEE_SEARCH_MAX_RESULTS);
  const showDropdown = dropdownOpen && (canSearch || !!error);

  const toggleSignee = (signee: Signee) => {
    onChange(
      selectedSignees.some((s) => s.id === signee.id)
        ? selectedSignees.filter((s) => s.id !== signee.id)
        : [...selectedSignees, signee]
    );
  };

  const addSignee = (result: QuerySignee) => {
    const signee: Signee = {
      id: result.id,
      contactId: String(result.id),
      name: result.name,
    };
    setAddedSignees((prev) => [...prev, signee]);
    onChange([...selectedSignees, signee]);
    setSearch("");
    setDropdownOpen(false);
  };

  const removeSignee = (id: number) => {
    setAddedSignees((prev) => prev.filter((s) => s.id !== id));
    onChange(selectedSignees.filter((s) => s.id !== id));
  };

  return (
    <div className="relative flex w-80 shrink-0 flex-col border-r border-gray-200 dark:border-gray-700">
      <div className="flex shrink-0 items-center justify-between border-b border-gray-200 px-4 py-3 dark:border-gray-700">
        <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">
          Signatarer
        </span>
        <span
          className={cn(
            "text-[10px] font-medium rounded-full px-2 py-0.5 leading-none",
            selectedSignees.length === 0
              ? "bg-amber-50 text-amber-700 dark:bg-amber-950/40 dark:text-amber-400"
              : "bg-blue-50 text-blue-700 dark:bg-blue-950/40 dark:text-blue-400"
          )}
        >
          {selectedSignees.length} av {availableSignees.length} valgt
        </span>
      </div>
      <div
        ref={searchAreaRef}
        className="relative shrink-0 border-b border-gray-100 px-4 py-3 dark:border-gray-800"
        onPointerDownCapture={() => {
          tabNavigationRef.current = false;
        }}
        onBlur={(event) => {
          // Native scrollbars may move focus to the body; only Tab should close on blur.
          if (
            tabNavigationRef.current &&
            (!(event.relatedTarget instanceof Node) ||
              !event.currentTarget.contains(event.relatedTarget))
          ) {
            setDropdownOpen(false);
          }
          tabNavigationRef.current = false;
        }}
        onKeyDown={(event) => {
          tabNavigationRef.current = event.key === "Tab";
          if (event.key === "Escape") {
            event.preventDefault();
            setDropdownOpen(false);
          }
        }}
      >
        <label
          htmlFor="signee-search"
          className="sr-only"
        >
          Søk etter flere signatarer
        </label>
        <div className="relative">
          <Search
            size={14}
            aria-hidden="true"
            className="pointer-events-none absolute left-2.5 top-2.5 text-muted-foreground"
          />
          <input
            id="signee-search"
            type="search"
            autoComplete="off"
            value={search}
            onChange={(event) => {
              setSearch(event.target.value);
              setDropdownOpen(true);
            }}
            onFocus={() => setDropdownOpen(true)}
            placeholder={`Søk med minst ${SIGNEE_SEARCH_MIN_LENGTH} tegn`}
            aria-describedby="signee-search-status"
            aria-controls={showDropdown ? "signee-search-results" : undefined}
            className="w-full rounded-lg border border-input bg-muted/30 py-2 pl-8 pr-2.5 text-xs text-foreground transition-colors placeholder:text-muted-foreground focus:bg-background focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <p id="signee-search-status" role="status" className="sr-only">
            {!canSearch
              ? `Skriv minst ${SIGNEE_SEARCH_MIN_LENGTH} tegn for å søke.`
              : loading
              ? "Henter signatarer..."
              : results.length === 0
              ? "Ingen treff."
              : `${results.length} treff`}
          </p>
          {showDropdown && (
            <div
              id="signee-search-results"
              role="region"
              aria-label="Søkeresultater"
              className="absolute left-0 top-full z-20 mt-1 w-[22rem] max-w-[calc(100vw-2rem)] overflow-hidden rounded-xl border border-gray-200 bg-popover text-popover-foreground shadow-xl ring-1 ring-black/5 animate-in fade-in-0 zoom-in-95 dark:border-gray-700 dark:ring-white/5"
            >
              {error ? (
                <p
                  role="alert"
                  className="px-3 py-2 text-xs text-red-600 dark:text-red-400"
                >
                  Kunne ikke hente signatarer. Prøv igjen ved å åpne
                  signeringsvisningen på nytt.
                </p>
              ) : loading ? (
                <p className="px-3 py-2 text-xs text-gray-500 dark:text-gray-400">
                  Henter signatarer...
                </p>
              ) : results.length === 0 ? (
                <p className="px-3 py-2 text-xs text-gray-500 dark:text-gray-400">
                  Ingen treff.
                </p>
              ) : (
                <>
                  <ul className="max-h-64 space-y-0.5 overflow-y-auto overscroll-contain p-1.5">
                    {visibleResults.map((result) => (
                      <li key={result.id}>
                        <button
                          type="button"
                          onClick={() => addSignee(result)}
                          className="group flex w-full items-center gap-2.5 rounded-lg px-2.5 py-1.5 text-left text-xs transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                        >
                          <span className="flex size-6 shrink-0 items-center justify-center rounded-full bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400">
                            <UserRound size={12} aria-hidden="true" />
                          </span>
                          <span className="min-w-0 flex-1 break-words font-medium">
                            {result.showData}
                          </span>
                          <Plus
                            size={14}
                            aria-hidden="true"
                            className="shrink-0 text-gray-400 transition-colors group-hover:text-blue-600 group-focus-visible:text-blue-600 dark:group-hover:text-blue-400 dark:group-focus-visible:text-blue-400"
                          />
                        </button>
                      </li>
                    ))}
                  </ul>
                  {results.length > SIGNEE_SEARCH_MAX_RESULTS && (
                    <p className="border-t border-gray-200 px-3 py-2 text-[11px] text-gray-500 dark:border-gray-700 dark:text-gray-400">
                      Viser {SIGNEE_SEARCH_MAX_RESULTS} av {results.length}{" "}
                      treff. Søk mer spesifikt for å finne flere.
                    </p>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto px-3 pb-10">
        <div
          aria-hidden="true"
          className="sticky top-0 z-10 grid grid-cols-[minmax(0,1fr)_3rem] items-center bg-card px-2.5 py-2 text-[10px] font-medium text-muted-foreground"
        >
          <span>Signatar</span>
          <span className="text-center">Signere</span>
        </div>
        <div className="space-y-1.5">
          {availableSignees.map((s) => {
            const selectedSignee = selectedSignees.find(
              (selected) => selected.id === s.id
            );
            const active = !!selectedSignee;
            const isManuallyAdded = !signees.some(
              (initial) => initial.id === s.id
            );
            return (
              <div
                key={s.id}
                className={cn(
                  "grid grid-cols-[minmax(0,1fr)_3rem] items-center rounded-lg border px-2.5 py-2 transition-colors",
                  active
                    ? "border-blue-200/80 bg-blue-50/70 dark:border-blue-900 dark:bg-blue-950/30"
                    : "border-border/60 bg-background hover:border-border hover:bg-muted/20"
                )}
              >
                <div className="flex min-w-0 items-center gap-2">
                  <span
                    aria-hidden="true"
                    className={cn(
                      "flex size-7 shrink-0 items-center justify-center rounded-full text-[10px] font-semibold",
                      active
                        ? "bg-teal-100 text-teal-700 dark:bg-teal-900/40 dark:text-teal-300"
                        : "bg-violet-100 text-violet-600 dark:bg-violet-900/30 dark:text-violet-300"
                    )}
                  >
                    {s.name.trim().split(/\s+/).map((part) => part.charAt(0)).slice(0, 2).join("").toLocaleUpperCase()}
                  </span>
                  <div className="min-w-0 flex-1">
                    <p
                      title={`${s.name} (${s.contactId})`}
                      className="truncate text-xs font-semibold leading-tight text-foreground"
                    >
                      {s.name}
                    </p>
                    <p className="mt-0.5 truncate text-[11px] text-muted-foreground">
                      {s.contactId}
                    </p>
                  </div>
                  {isManuallyAdded && (
                    <button
                      type="button"
                      aria-label={`Fjern ${s.name}`}
                      title={`Fjern ${s.name}`}
                      onClick={() => removeSignee(s.id)}
                      className="shrink-0 rounded p-1 text-muted-foreground hover:bg-destructive/10 hover:text-destructive focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    >
                      <X size={14} aria-hidden="true" />
                    </button>
                  )}
                </div>
                  <label
                    title={`Skal signere: ${s.name}`}
                    className="flex min-h-8 cursor-pointer items-center justify-center"
                  >
                    <input
                      type="checkbox"
                      checked={active}
                      onChange={() => toggleSignee(s)}
                      className="size-3.5 rounded accent-blue-600"
                    />
                    <span className="sr-only">Skal signere: {s.name}</span>
                  </label>
              </div>
            );
          })}
        </div>
      </div>
      <div
        className={cn(
          "pointer-events-none absolute bottom-3 left-1/2 z-10 -translate-x-1/2 transition-all duration-300 ease-out",
          selectedSignees.length === 0
            ? "translate-y-0 opacity-100"
            : "translate-y-4 opacity-0"
        )}
        aria-hidden={selectedSignees.length > 0}
        role="status"
      >
        <div className="relative whitespace-nowrap rounded-md bg-gray-900 px-3 py-2 text-[11px] font-medium text-white shadow-lg dark:bg-gray-800">
          <span className="absolute -top-1.5 left-1/2 size-0 -translate-x-1/2 border-x-[6px] border-b-[6px] border-x-transparent border-b-gray-900 dark:border-b-gray-800" />
          Velg minst én signant
        </div>
      </div>
    </div>
  );
};
